import React, { useState, useEffect } from "react";
import "./App.css";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { setEmail, fetchExp, fetchIdToken, authorization, fetchOrUpdateIdToken } from "./auth/Authorization";
import { createContext } from "react";
import Dashboard from "./components/Dashboard";
import SessionTimeoutModal from "./components/Common/SessionTimeoutModal";
import initializeGlobalFunctions from "./utils/globalFunctions";
import { startSessionTimeout, clearSession } from "./utils/sessionTimeout";
import { registerSession, consumeSessionExpiredFlag } from "./utils/sessionManager";
import { localConfig } from "./config/localConfig";
import { validateEnvironmentConfig, getEnvironmentInfo } from "./config/envValidator";

export const ThemeContext = createContext("dark");

// The session identifier used for single-active-session enforcement. It is
// generated lazily per browser/tab by sessionManager (persisted in
// sessionStorage); re-exported here for backward compatibility.
export { currentSessionID } from "./utils/sessionManager";

function isInIframe() {
	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
}

// Helper functions for cookie handling
function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop().split(";").shift();
	return null;
}

// Function to determine the source value based on iframe status and referrer cookie
function getSourceValue() {
	if (isInIframe()) {
		const referrerCookie = getCookie('referrer');
		return referrerCookie || 'system'; // fallback to system if cookie not found
	}
	return 'system';
}

// Parse auth_token cookie and extract accessToken and expiry
function getAuthDataFromCookie() {
	const authTokenCookie = getCookie("userToken");
	const usernameCookie = getCookie("userName");
	
	if (!authTokenCookie) {
		console.log("[Cookie Auth] No userToken cookie found");
		return null;
	}

	try {
		// userToken cookie contains the token directly
		const idToken = decodeURIComponent(authTokenCookie);
		const username = decodeURIComponent(usernameCookie);
		
		if (!idToken || idToken.trim() === '') {
			console.log("[Cookie Auth] userToken cookie is empty");
			return null;
		}

		if( !username || username.trim() === '') {
			console.log("[Cookie Auth] username cookie is empty");
			return null;
		}

		// For cookie-based auth, we'll set a reasonable expiry time
		// since we don't have expiry info in the cookie
		const currentTime = Math.floor(Date.now() / 1000);
		const exp = currentTime + (24 * 60 * 60); // 24 hours from now

		return {
			idToken: idToken,
			exp: exp,
			username: username
		};
	} catch (error) {
		console.error("[Cookie Auth] Error processing userToken cookie:", error);
		return null;
	}
}

// Component that handles MSAL authentication
function MsalAuthComponent({ setUserData }) {
	useMsalAuthentication(InteractionType.Redirect);
	const { accounts } = useMsal();
	
	useEffect(() => {
		try {
			if (accounts && accounts.length > 0) {
				const username = accounts[0].username;
				const idToken = accounts[0].idToken;
				const exp = accounts[0].idTokenClaims.exp;
				
				// Store tokens
				var curr_idToken;
				var curr_exp;
				if (idToken) {
					sessionStorage.setItem("idToken", idToken);
					sessionStorage.setItem("exp", exp);
				}
				curr_idToken = sessionStorage.getItem("idToken");
				curr_exp = sessionStorage.getItem("exp");
				
				setUserData({
					username,
					idToken: curr_idToken,
					exp: curr_exp
				});
			}
		} catch (e) {
			console.log(e);
		}
	}, [accounts, setUserData]);
	
	return null;
}

function App() {
	const [m_strUser, setm_strUser] = useState("");
	const [theme, setTheme] = useState("dark");
	const [idToken, setIdToken] = useState("");
	const [exp, setIdTokenExp] = useState("");
	const [authInitialized, setAuthInitialized] = useState(false);
	const [skipSSO, setSkipSSO] = useState(false);
	const [hasAccess, setHasAccess] = useState(null); // null = checking, true = has access, false = no access
	const [showSessionTimeoutWarning, setShowSessionTimeoutWarning] = useState(false);
	// True when this session was invalidated because the same account signed in
	// from another browser/device (see sessionManager.handleSessionExpired).
	const [forceSignedOut, setForceSignedOut] = useState(() => consumeSessionExpiredFlag());

	const toggleTheme = () => {
		setTheme(theme === "dark" ? "light" : "dark");
	};

	const setUserData = ({ username, idToken: newIdToken, exp: newExp }) => {
		setm_strUser(username);
		setIdToken(newIdToken);
		setIdTokenExp(newExp);
		sessionStorage.setItem("idToken", newIdToken);
		sessionStorage.setItem("exp", newExp);
	};

	// Initialize global functions on app start
	useEffect(() => {
		initializeGlobalFunctions();
		
		// Validate environment configuration
		const envValidation = validateEnvironmentConfig();
		const envInfo = getEnvironmentInfo();
		
		
		if (!envValidation.isValid && !localConfig.skipAuth) {
			console.error('❌ Environment configuration validation failed');
			console.error('Missing variables:', envValidation.missingVars);
		}
		
		// For local development, bypass authentication
		if (localConfig.skipAuth) {
			setm_strUser("utkarsh.chaturvedi@piramal.com");
			setIdToken("gYaLgd87_XeFWA1iCR8gTEXUWZyX493FSK_uQN4FaK8=");
			setIdTokenExp(Math.floor(Date.now() / 1000) + 3600); // 1 hour from now
			setAuthInitialized(true);
			setHasAccess(true); // Skip authorization check for local dev
			return;
		}

		if (isInIframe()) {
			console.log("[Cookie Auth] App running in iframe, checking for existing authentication cookies...");

			const cookieAuth = getAuthDataFromCookie();

			setUserData({
				username: cookieAuth.username,
				idToken: cookieAuth.idToken,
				exp: cookieAuth.exp
			}); 

			setSkipSSO(true);
			setAuthInitialized(true);
			return;
		} else {
			console.log("[Cookie Auth] No valid authentication found in cookies, proceeding with SSO");
		}

		setAuthInitialized(true);
	}, []);

	// Check authorization once we have idToken and exp
	useEffect(() => {
		const checkAuthorization = async () => {
			if (!idToken || !exp) {
				return; // Wait until we have credentials
			}

			// Skip auth check for local development
			if (localConfig.skipAuth) {
				setHasAccess(true);
				return;
			}

			try {
				const app_name = localConfig.appName;
				const validatedIdToken = await fetchOrUpdateIdToken(idToken, exp);
				const authResult = await authorization(validatedIdToken.idToken, app_name);
				
				setHasAccess(authResult.hasAccess);

				// Register this browser as the user's single active session. The
				// backend overwrites any previously registered session for this
				// user, which invalidates an older sign-in from another browser
				// on its next API call.
				if (authResult.hasAccess) {
					await registerSession(m_strUser);
				}
			} catch (error) {
				console.error("Authorization check failed:", error);
				setHasAccess(false);
			}
		};

		checkAuthorization();
	}, [idToken, exp]);

	// Session timeout: enforce inactivity-based session expiry
	// Security: prevents indefinite session reuse (Application Security Assessment)
	useEffect(() => {
		// Only enforce timeout when the user is authenticated and has access
		if (m_strUser === "" || hasAccess !== true) {
			return;
		}

		// Skip timeout enforcement for local development (auth is bypassed)
		if (localConfig.skipAuth) {
			return;
		}

		// Start the inactivity monitor. Default: 30 min inactivity -> warn,
		// then 60s later -> force logout.
		const stopSessionTimeout = startSessionTimeout({
			timeoutMs: 30 * 60 * 1000, // 30 minutes of inactivity
			warningMs: 60 * 1000, // warn 60 seconds before timeout
			onWarning: () => setShowSessionTimeoutWarning(true),
			onExpire: () => {
				setShowSessionTimeoutWarning(false);
				// endSession() (default) clears storage/cookies and reloads
			},
		});

		// Cleanup when the component unmounts or auth state changes
		return () => {
			stopSessionTimeout();
			setShowSessionTimeoutWarning(false);
		};
	}, [m_strUser, hasAccess]);

	// If this session was invalidated because the same account was signed in
	// from another browser/device, gate the app behind a sign-in-again screen.
	if (forceSignedOut) {
		return (
			<div style={{
				height: '100vh',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '48px',
				backgroundColor: 'white'
			}}>
				<div style={{
					textAlign: 'center',
					padding: '48px',
					backgroundColor: 'white',
					borderRadius: '12px',
					boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
					maxWidth: '500px',
					margin: '20px'
				}}>
					<svg
						style={{
							width: '64px',
							height: '64px',
							margin: '0 auto 24px',
							color: '#ef4444'
						}}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
						/>
					</svg>
					<h1 style={{
						fontSize: '24px',
						fontWeight: '600',
						color: '#111827',
						marginBottom: '16px'
					}}>
						Signed In Elsewhere
					</h1>
					<p style={{
						fontSize: '16px',
						color: '#6b7280',
						marginBottom: '24px',
						lineHeight: '1.6'
					}}>
						This session has ended because the same account was used to
						sign in from another browser or device. Please sign in again
						to continue.
					</p>
					<button
						onClick={() => {
							try {
								clearSession();
							} catch (e) {
								console.warn(e);
							}
							window.location.reload();
						}}
						style={{
							padding: '12px 24px',
							backgroundColor: '#8B5E3C',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							fontSize: '14px',
							fontWeight: '500',
							cursor: 'pointer',
							transition: 'background-color 0.2s'
						}}
						onMouseOver={(e) => e.target.style.backgroundColor = '#7a5235'}
						onMouseOut={(e) => e.target.style.backgroundColor = '#8B5E3C'}
					>
						Sign In Again
					</button>
				</div>
			</div>
		);
	}

	if (!authInitialized) {
		return (
			<div style={{ 
				height: '100vh', 
				display: 'flex', 
				justifyContent: 'center', 
				alignItems: 'center', 
				fontSize: '15px',
				backgroundColor: 'white',
				color: '#333'
			}}>
				<div className="text-center">
					<div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
					<p>Initializing authentication...</p>
				</div>
			</div>
		);
	}


	// Show loading state while checking authorization
	if (m_strUser !== "" && hasAccess === null) {
		return (
			<div style={{ 
				height: '100vh', 
				display: 'flex', 
				justifyContent: 'center', 
				alignItems: 'center', 
				fontSize: '15px',
				backgroundColor: 'white',
				color: '#333'
			}}>
				<div className="text-center">
					<div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
					<p>Checking authorization...</p>
				</div>
			</div>
		);
	}

	// Show full-screen access denied error when hasAccess is false
	if (hasAccess === false) {
		return (
			<div style={{ 
				height: '100vh', 
				width: '100vw',
				display: 'flex', 
				justifyContent: 'center', 
				alignItems: 'center', 
				backgroundColor: '#f9fafb',
				position: 'fixed',
				top: 0,
				left: 0,
				zIndex: 9999
			}}>
				<div style={{
					textAlign: 'center',
					padding: '48px',
					backgroundColor: 'white',
					borderRadius: '12px',
					boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
					maxWidth: '500px',
					margin: '20px'
				}}>
					<svg
						style={{
							width: '64px',
							height: '64px',
							margin: '0 auto 24px',
							color: '#ef4444'
						}}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
						/>
					</svg>
					<h1 style={{ 
						fontSize: '24px', 
						fontWeight: '600', 
						color: '#111827',
						marginBottom: '16px' 
					}}>
						Access Denied
					</h1>
					<p style={{ 
						fontSize: '16px', 
						color: '#6b7280',
						marginBottom: '24px',
						lineHeight: '1.6'
					}}>
						You do not have permission to access this application.
						<br />
						Please contact your administrator for access.
						<br />
						<u><b>If you were able to access CAM Copilot earlier, please try loading this link with your mobile hotspot.</b></u>
					</p>
					<div style={{
						backgroundColor: '#fef2f2',
						border: '1px solid #fecaca',
						borderRadius: '8px',
						padding: '16px',
						marginTop: '24px'
					}}>
						<p style={{
							fontSize: '14px',
							color: '#991b1b',
							margin: 0
						}}>
							<strong>User:</strong> {m_strUser}
						</p>
					</div>
					<button
						onClick={() => window.location.reload()}
						style={{
							marginTop: '24px',
							padding: '12px 24px',
							backgroundColor: '#8B5E3C',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							fontSize: '14px',
							fontWeight: '500',
							cursor: 'pointer',
							transition: 'background-color 0.2s'
						}}
						onMouseOver={(e) => e.target.style.backgroundColor = '#7a5235'}
						onMouseOut={(e) => e.target.style.backgroundColor = '#8B5E3C'}
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	if (m_strUser !== "" && hasAccess === true) {
		setEmail(m_strUser);
		fetchIdToken(idToken);
		fetchExp(exp);
		return (
			<ThemeContext.Provider value={{ theme, toggleTheme }}>
				<Dashboard 
					emailID={m_strUser}
					idToken={idToken}
					exp={exp}
					source={getSourceValue()}
					hasAccess={hasAccess}
				/>
				<SessionTimeoutModal
					open={showSessionTimeoutWarning}
					onClose={() => setShowSessionTimeoutWarning(false)}
				/>
			</ThemeContext.Provider>
		);
	} else {
		return (
			<>
				{!localConfig.skipAuth && !skipSSO && <MsalAuthComponent setUserData={setUserData} />}
				<div style={{ 
					height: '100vh', 
					display: 'flex', 
					justifyContent: 'center', 
					alignItems: 'center', 
					fontSize: '15px',
					backgroundColor: 'white',
					color: '#333'
				}}>
					<div className="text-center">
						<div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
						<p>Please wait...</p>
					</div>
				</div>
			</>
		);
	}
}

export default App;